import { createSlice } from '@reduxjs/toolkit';
import { circleCheckConfig } from 'config';
import {
    applySavedChangesToBaseline,
    buildLocationGateIdsMap,
    getBaselineMapping,
    isSameMapping,
    mappingKey,
    normalizeMapping,
} from 'utils/saturationChanges';

const initialState = {
    allDesks: [],
    filteredDesks: [],
    allDeskLocations: [],
    savedDesk: [],
    allDivisions: [],
    currentDesk: {},
    isActiveDeskConfirmed: true,
    fetchingDesksError: false,
    fetchingDesksInProgress: undefined,
    fetchingDesksFinished: false,
    fetchingLocationsInProgress: undefined,
    fetchingSavedDeskInProgress: undefined,
    fetchingDivisionsInProgress: undefined,
    fetchingDivisionByIdInProgress: undefined,
    fetchingLocationByDivisionInProgress: undefined,
    fetchingSavedDesksError: false,
    fetchingDivisionsError: false,
    fetchingDivisionByIdError: false,
    fetchingLocationByDivisionError: false,
    isDeskLoading: false,
    maxDeskTransactions: 0,
    expectedFinishedTransactionMillis: 0,
    circleCheckRotationIntervalMillis: circleCheckConfig.DEFAULT_ROTATION_INTERVAL_MS,
    videoWallCameraWarningLimit: null,
    totalStreamingCameraWarningLimit: null,
    deskWarnings: { warnings: [] },
    pauseRequestSent: false,
    pendingSaturationChanges: {},
    savingSaturationChanges: {},
    savingSaturationInProgress: false,
    notFoundFilteredDeskMessage: '',
    isFetchingSaturationInfo: true,
    isLoadingDeskSaturationPage: true,
    filterValues: {
        divisionValue: '',
        deskValue: '',
        locationValue: '',
    },
    showSkeleton: false,
};

// Drops every pending change that already matches the persisted baseline, so Save only lights up
// for mappings that would actually change something.
const dropPendingMatchingBaseline = (state) => {
    const gateIdsByLocation = buildLocationGateIdsMap(state.allDeskLocations);

    Object.entries(state.pendingSaturationChanges).forEach(([key, mapping]) => {
        const baseline = getBaselineMapping(state.savedDesk, mapping.deskId, mapping.locationId, gateIdsByLocation[mapping.locationId]);
        if (isSameMapping(mapping, baseline)) {
            delete state.pendingSaturationChanges[key];
        }
    });
};

const desksSlice = createSlice({
    name: 'desks',
    initialState,
    reducers: {
        fetchDesksStart(state) {
            state.fetchingDesksInProgress = true;
            state.fetchingDesksError = false;
            state.fetchingDesksFinished = false;
        },

        fetchLocationsStart(state) {
            state.fetchingLocationsInProgress = true;
            state.fetchingDesksError = false;
        },

        fetchSavedDeskStart(state) {
            state.fetchingSavedDeskInProgress = true;
            state.fetchingSavedDesksError = false;
        },

        fetchDivisionsStart(state) {
            state.fetchingDivisionsInProgress = true;
            state.fetchingDivisionsError = false;
        },

        fetchDivisionByIdStart(state) {
            state.fetchingDivisionByIdInProgress = true;
            state.fetchingDivisionByIdError = false;
        },

        fetchLocationByDivisionStart(state) {
            state.fetchingLocationByDivisionInProgress = true;
            state.fetchingLocationByDivisionError = false;
        },

        fetchDesksSuccess(state, { payload }) {
            state.fetchingDesksInProgress = false;
            state.isFetchingSaturationInfo = false;
            state.fetchingDesksFinished = true;
            state.allDesks = payload;
            state.filteredDesks = payload;
            state.showSkeleton = false;
        },

        fetchLocationsSuccess(state, { payload }) {
            state.fetchingLocationsInProgress = false;
            state.allDeskLocations = payload;
        },

        fetchSavedDesksSuccess(state, { payload }) {
            state.fetchingSavedDeskInProgress = false;
            state.savedDesk = payload;
        },

        fetchDivisionsSuccess(state, { payload }) {
            state.fetchingDivisionsInProgress = false;
            state.allDivisions = payload;
        },

        fetchDivisionByIdSuccess(state, { payload }) {
            state.fetchingDivisionByIdInProgress = false;
            state.allDesks = payload;
        },

        fetchLocationByDivisionSuccess(state, { payload }) {
            state.fetchingLocationByDivisionInProgress = false;
            state.allDeskLocations = payload;
        },

        fetchSavedDesksFailed(state, { payload }) {
            state.fetchingSavedDeskInProgress = false;
            state.fetchingSavedDesksError = payload;
        },

        fetchDesksFailed(state, { payload }) {
            state.fetchingDesksInProgress = false;
            state.fetchingDesksError = payload;
        },

        fetchDivisionsFailed(state, { payload }) {
            state.fetchingDivisionsInProgress = false;
            state.fetchingDivisionsError = payload;
        },

        fetchDivisionByIdFailed(state, { payload }) {
            state.fetchingDivisionByIdInProgress = false;
            state.fetchingDivisionByIdError = payload;
        },

        fetchLocationByDivisionFailed(state, { payload }) {
            state.fetchingLocationByDivisionInProgress = false;
            state.fetchingLocationByDivisionError = payload;
        },

        setCurrentDeskInactive(state) {
            state.allDesks.forEach((desk) => {
                if (desk.id === state.currentDesk.id) desk.status = 'INACTIVE';
            });
        },

        setCurrentDesk(state, { payload: desk }) {
            state.currentDesk = desk;
            if (desk) state.pauseRequestSent = false;
            if (!Object.keys(desk).length) state.isActiveDeskConfirmed = true;
        },

        selectDesk() {},

        // Saga trigger: PUT the per-desk activity-tracking flag (BEAST-13284). Payload { id, enabled }.
        toggleDeskActivityEventsEnabled() {},

        // Flips the per-desk activity-tracking flag in place. A targeted field update, NOT replaceDesk:
        // the toggle endpoint returns the bare Desk entity (no sessions/locations/assignedToMe), so
        // only the boolean is touched here, on the matching list rows and on currentDesk when it matches.
        setDeskActivityEventsEnabled(state, { payload: { id, enabled } }) {
            state.allDesks.forEach((desk) => {
                if (desk.id === id) desk.activityEventsEnabled = enabled;
            });
            state.filteredDesks.forEach((desk) => {
                if (desk.id === id) desk.activityEventsEnabled = enabled;
            });
            if (state.currentDesk?.id === id) state.currentDesk.activityEventsEnabled = enabled;
        },

        // Intent only: the saga decides whether there is anything to submit.
        saveDeskInfo() {},

        setMaxDeskTransactions(state, { payload }) {
            state.maxDeskTransactions = payload;
        },

        setExpectedFinishedTransactionMillis(state, { payload }) {
            state.expectedFinishedTransactionMillis = payload;
        },

        setCircleCheckRotationIntervalMillis(state, { payload }) {
            state.circleCheckRotationIntervalMillis = payload ?? circleCheckConfig.DEFAULT_ROTATION_INTERVAL_MS;
        },

        setVideoWallCameraWarningLimit(state, { payload }) {
            state.videoWallCameraWarningLimit = payload ?? null;
        },

        setTotalStreamingCameraWarningLimit(state, { payload }) {
            state.totalStreamingCameraWarningLimit = payload ?? null;
        },

        replaceDesk(state, { payload: deskToReplace }) {
            state.allDesks = state.allDesks.map((desk) =>
                desk.id === deskToReplace.id ? { ...deskToReplace, ...(desk.locations && { locations: desk.locations }) } : desk
            );
            // Keep the current desk's activity-tracking flag live when the all-desks broadcast carries a
            // new per-desk setting (BEAST-13284). Targeted field sync only - currentDesk holds richer
            // data (locations, sessions) the broadcast DeskInfo does not, so never replace the whole
            // object here. Guarded on presence so a broadcast without the field cannot blank the gate.
            if (state.currentDesk?.id === deskToReplace.id && deskToReplace.activityEventsEnabled !== undefined) {
                state.currentDesk.activityEventsEnabled = deskToReplace.activityEventsEnabled;
            }
        },
        setIsActiveDeskConfirmed(state, { payload: isActiveDeskConfirmed }) {
            state.isActiveDeskConfirmed = isActiveDeskConfirmed;
        },

        setDeskWarnings(state, { payload: { deskWarnings, lastChecked } }) {
            state.deskWarnings.warnings = deskWarnings.reduce((warnings, { args, code }) => {
                if (code === 'INACTIVE_DESK_WITH_TRANSACTIONS') {
                    warnings.push(`${args[0]} (${args[1]})`);
                }
                state.deskWarnings.lastChecked = lastChecked;
                return warnings;
            }, []);
            state.isLoadingDeskSaturationPage = false;
        },

        setIsDeskLoading(state, { payload: isLoading }) {
            state.isDeskLoading = isLoading;
        },

        setPauseRequestSent(state, { payload: pauseRequestSent }) {
            state.pauseRequestSent = pauseRequestSent;
        },

        checkDeskWarnings() {},

        /**
         * Records the desired state of one location-to-desk mapping. A mapping that is changed
         * back to its persisted value stops being a change and is dropped from the set.
         *
         * While the mapping's own save is in flight, its outcome is unknown, so the desired value is
         * always recorded; saveSaturationSuccess / saveSaturationFailed reconcile it once the request
         * settles. Dropping it here would blank the checkbox and lose the change if the save fails.
         */
        toggleSaturationMapping(state, { payload }) {
            const { deskId, deskName, locationId, locationName, allGates, gateIds, locationGateIds } = payload;
            const key = mappingKey(deskId, locationId);
            const desired = normalizeMapping({ allGates, gateIds }, locationGateIds);
            const isSaving = Boolean(state.savingSaturationChanges[key]);

            if (!isSaving && isSameMapping(desired, getBaselineMapping(state.savedDesk, deskId, locationId, locationGateIds))) {
                delete state.pendingSaturationChanges[key];
                return;
            }

            state.pendingSaturationChanges[key] = { deskId, deskName, locationId, locationName, ...desired };
        },

        clearSaturationChanges(state) {
            state.pendingSaturationChanges = {};
        },

        saveSaturationStart(state, { payload: submittedChanges }) {
            state.savingSaturationInProgress = true;
            state.savingSaturationChanges = submittedChanges;
            state.showSkeleton = true;
        },

        /**
         * The backend stored exactly what was sent, so the submitted changes become the new
         * baseline and stop being pending. Mappings changed while the request was in flight are kept.
         */
        saveSaturationSuccess(state) {
            state.savedDesk = applySavedChangesToBaseline(state.savedDesk, state.savingSaturationChanges);

            Object.entries(state.savingSaturationChanges).forEach(([key, mapping]) => {
                if (isSameMapping(state.pendingSaturationChanges[key], mapping)) {
                    delete state.pendingSaturationChanges[key];
                }
            });

            dropPendingMatchingBaseline(state);

            state.savingSaturationChanges = {};
            state.savingSaturationInProgress = false;
            state.showSkeleton = false;
        },

        // Unsaved changes are kept, so the user can retry. A change reverted to the persisted value
        // while the request was in flight is no longer a change.
        saveSaturationFailed(state) {
            dropPendingMatchingBaseline(state);
            state.savingSaturationChanges = {};
            state.savingSaturationInProgress = false;
            state.showSkeleton = false;
        },

        findDesk(state, { payload }) {
            if (!payload || payload.trim() === '') {
                state.allDesks = [...state.filteredDesks];
                state.notFoundFilteredDeskMessage = '';
            } else {
                const filteredDesks = state.filteredDesks.filter((desk) => desk.name.toLowerCase().includes(payload.toLowerCase()));
                state.allDesks = filteredDesks.length > 0 ? filteredDesks : state.allDesks;
                state.notFoundFilteredDeskMessage = filteredDesks.length === 0 ? 'No desk found. Try another keyword.' : '';
            }
        },

        setFilterValues(state, { payload }) {
            const { divisionValue, deskValue, locationValue } = payload;
            state.filterValues = {
                divisionValue,
                deskValue,
                locationValue,
            };
        },
    },
});

export const desksReducer = desksSlice.reducer;
export const desksActions = desksSlice.actions;
